"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.SegmentChart = SegmentChart;
var _hooks = require("@a1s/hooks");
var _shape = require("@visx/shape");
var _scale = require("@visx/scale");
var _react = _interopRequireWildcard(require("react"));
var _styled = require("../../styled");
var _base = require("../../base");
var _components = require("../../components");
var _animations = require("../../shared/animations");
var _styled2 = require("./styled");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const MINIMUM_BAR_PIXEL_SIZE = 5;
const BAR_PIXEL_GAP = 2;
//
// Main component
// -------------------------------------------------------------------------------------------------
function SegmentChart({
  children,
  direction = 'vertical',
  height,
  testId,
  width
}) {
  const baseRef = (0, _react.useRef)(null);
  const [chartOffsets, setChartOffsets] = (0, _react.useState)({
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });
  const {
    height: useDimensionsHeight,
    width: useDimensionsWidth
  } = (0, _hooks.useDimensions)(baseRef);
  const [mutation, mutationRef, {
    current: mutationElement
  }] = (0, _hooks.useMutations)({
    attributes: true,
    attributeFilter: ['class']
  });
  const [childWidths, setChildWidths] = (0, _react.useState)([]);
  const [childLineOffsets, setChildLineOffsets] = (0, _react.useState)([]);

  // Calculate the size of child elements
  (0, _react.useEffect)(() => {
    if (!baseRef.current || direction !== 'horizontal') return;
    const captions = baseRef.current.querySelectorAll(_styled2.StyledCaption.selector);
    const children = captions.length > 0 ? Array.from(captions) : [];
    setChildWidths(children
    // Result of getClientRects could be undefined if Jest is used, in this case set a test value
    .map(label => {
      var _label$getClientRects;
      return ((_label$getClientRects = label.getClientRects()[0]) === null || _label$getClientRects === void 0 ? void 0 : _label$getClientRects.width) | 0;
    }));
    // Look for an optional handler to attach the line to
    setChildLineOffsets(children.map(label => {
      var _label$querySelector, _lineHandle$getClient;
      const lineHandle = (_label$querySelector = label.querySelector(_styled2.LineHandle.selector)) !== null && _label$querySelector !== void 0 ? _label$querySelector : label;
      return (((_lineHandle$getClient = lineHandle.getClientRects()[0]) === null || _lineHandle$getClient === void 0 ? void 0 : _lineHandle$getClient.width) | 0) / 2;
    }));
  }, [children, direction]);
  const [backgroundPadding, setBackgroundPadding] = (0, _react.useState)(0);
  (0, _react.useEffect)(() => {
    const target = mutation === null ? mutationElement : mutation.target;
    if (mutationElement === null) return;
    const pixels = window.getComputedStyle(target).getPropertyValue('stroke-width');
    const px = parseInt(pixels.replace('px', ''));
    setBackgroundPadding(px);
    setChartOffsets({
      x: px / 2,
      y: px / 2,
      width: -px,
      height: -px
    });
  }, [mutation, mutationElement]);
  if (!children) return null;
  if (!width) width = direction === 'vertical' ? 24 : useDimensionsWidth;
  if (!height) height = direction === 'vertical' ? useDimensionsHeight : 24;
  if (typeof width === 'string') width = Number.parseInt(width, 10);
  if (typeof height === 'string') height = Number.parseInt(height, 10);

  // Round up all of the values from the segments.
  const values = _react.default.Children.map(children, ({
    props: {
      value
    }
  }) => {
    return typeof value === 'string' ? Number.parseInt(value, 10) : value;
  }).reverse();
  const sum = values.reduce((acc, v) => acc + v, 0);
  const valuesToChartData = d => d.reduce((acc, v, i) => ({
    ...acc,
    [i]: v
  }), {});

  // Shift the data to create a minimum bar size

  const minimumBarSizePercentage = parseInt((100 * MINIMUM_BAR_PIXEL_SIZE / (direction === 'vertical' ? height : width)).toFixed(1));
  const minimumBarValue = parseInt((sum / 100 * minimumBarSizePercentage).toFixed(0));
  const dataShiftedDeltas = new Array(values.length).fill(0);

  // Give minimum values to bars, so visx can handle scaling and positioning
  const shiftData = (value, index) => {
    if (value > minimumBarValue) {
      return value;
    }
    dataShiftedDeltas[index] = minimumBarValue - value;
    return minimumBarValue;
  };
  const unshiftedData = valuesToChartData(values);
  const unshiftBarStack = bar => {
    var _unshiftedData;
    const {
      index
    } = bar;
    bar.bars[0].bar = [(_unshiftedData = unshiftedData[index - 1]) !== null && _unshiftedData !== void 0 ? _unshiftedData : 0, unshiftedData[index]];
    bar.bars[0].bar.data = unshiftedData;
    return bar;
  };

  // Store the values into a data object like how the `<BarStack>` component wants it.
  const data = valuesToChartData(values.map(shiftData));
  const chartDomainDelta = dataShiftedDeltas.reduce((a, b) => a + b);
  dataShiftedDeltas.reverse();

  // The keys, in this case, are just the index-order of how the Segment children where passed in.
  const keys = Object.keys(data);

  // Collect the colors for each segment so we can create a scale for `<BarStack>` to use.
  const colors = _react.default.Children.map(children, child => child.props.color).reverse();
  const colorScale = (0, _scale.scaleOrdinal)({
    domain: keys,
    range: colors
  });

  // Calculate our sums from the data being passed in.
  const xScale = direction == 'vertical' ? (0, _scale.scaleBand)().range([0, width + chartOffsets.width]) : (0, _scale.scaleLinear)({
    domain: [0, sum + chartDomainDelta]
  }).range([0, width + chartOffsets.width]);
  const yScale = direction == 'vertical' ? (0, _scale.scaleLinear)({
    domain: [0, sum + chartDomainDelta]
  }).range([height + chartOffsets.height, 0]) : (0, _scale.scaleBand)().range([0, height + chartOffsets.height]);

  // Calculate width of lines connecting to labels.
  const lineWidth = width * 1.5;
  const lineOffsetX = 5 + backgroundPadding / 2;

  // - Horizontal only
  const lineHeight = height * 1.1;
  const lineOffsetY = 2;

  // Calculate line widths - left, center, right.
  const lineRatios = [15, 35, 50];
  const lineWidths = lineRatios.map(value => lineWidth / 100 * value);

  // - Horizontal only
  const lineRatiosHorizontal = [15, 70, 15];
  const lineHeights = lineRatiosHorizontal.map(value => lineHeight / 100 * value);

  // Adjust SVG width to make room for lines.
  const vectorWidth = width + lineWidth + lineOffsetX;

  // Horizontal only
  const vectorHeight = height + lineHeight + lineOffsetY;
  if (direction == 'horizontal') {
    const totalChildWidths = childWidths.length > 0 ? childWidths.reduce((a, b) => a + b) : 0;
    const spaceBetween = (useDimensionsWidth - totalChildWidths) / (children.length + 1);
    let labelX = 0;
    return /*#__PURE__*/_react.default.createElement(_styled2.StyledBaseHorizontal, {
      ref: baseRef,
      "data-testid": testId
    }, /*#__PURE__*/_react.default.createElement(_styled2.StyledCaptionsContainerHorizontal, null, children), /*#__PURE__*/_react.default.createElement(_styled2.StyledSVG, {
      width: width,
      height: vectorHeight
    }, /*#__PURE__*/_react.default.createElement(_shape.BarStackHorizontal, {
      data: [data],
      keys: keys.reverse(),
      y: () => null,
      xScale: xScale,
      yScale: yScale,
      color: colorScale
    }, barStacks => {
      const backgroundWidth = barStacks.reduce((value, bar) => {
        return value + bar.bars[0].width;
      }, 0);
      return [/*#__PURE__*/_react.default.createElement(_styled2.StyledBackground, {
        height: height - backgroundPadding,
        key: "background",
        ref: mutationRef,
        rx: 3,
        width: backgroundWidth - backgroundPadding / 2,
        x: backgroundPadding / 2,
        y: lineHeight + lineOffsetY + backgroundPadding / 2
      }), ...barStacks.map(barStack => unshiftBarStack(barStack).bars.map(({
        width,
        height,
        index,
        x,
        y,
        color
      }) => {
        const barWidth = width - 2;

        // Calculate the border radius based on the height of the bar.
        let rx = 3;
        if (barWidth <= 3) rx = 1;else if (barWidth <= 6) rx = 1.5;
        const lineStartX = labelX + spaceBetween + (childLineOffsets[barStack.index] | 0);
        labelX = labelX + spaceBetween + (childWidths[barStack.index] | 0);
        const [start, middle, end] = lineHeights;
        return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, {
          key: `bar-stack-${barStack.index}-${index}`
        }, /*#__PURE__*/_react.default.createElement(_styled2.StyledRect, {
          fill: color,
          height: height,
          rx: rx,
          x: x + backgroundPadding / 2,
          y: y + lineHeight + lineOffsetY + backgroundPadding / 2,
          width: barWidth
        }), /*#__PURE__*/_react.default.createElement(_styled2.StyledCaptionLine, {
          d: `M ${lineStartX} ${lineOffsetY} l 0 ${start} L ${x + barWidth / 2} ${middle} l 0 ${end}`
        }));
      }))];
    })));
  }
  return /*#__PURE__*/_react.default.createElement(_styled2.StyledBase, {
    ref: baseRef,
    "data-testid": testId
  }, /*#__PURE__*/_react.default.createElement(_styled2.StyledSVG, {
    width: vectorWidth - backgroundPadding / 2,
    height: height
  }, /*#__PURE__*/_react.default.createElement(_shape.BarStack, {
    data: [data],
    keys: keys,
    x: () => null,
    xScale: xScale,
    yScale: yScale,
    color: colorScale
  }, barStacks => {
    const backgroundHeight = barStacks.reduce((value, bar) => {
      return value + bar.bars[0].height;
    }, 0);
    return [/*#__PURE__*/_react.default.createElement(_styled2.StyledBackground, {
      height: backgroundHeight - backgroundPadding / 2,
      key: "background",
      ref: mutationRef,
      rx: 3,
      width: width - backgroundPadding,
      x: backgroundPadding / 2,
      y: backgroundPadding / 2
    }), ...barStacks.map(barStack => unshiftBarStack(barStack).bars.map(bar => {
      const barHeight = bar.height - BAR_PIXEL_GAP;

      // Calculate the border radius based on the height of the bar.
      let rx = 3;
      if (barHeight <= 3) rx = 1;else if (barHeight <= 6) rx = 1.5;

      // Calculate the size of an individual label box.
      const labelBoxHeight = height / (barStacks.length * 2 - 1);

      // Calculate the index in reverse, since rendering happens from bottom to top.
      const labelIndex = barStacks.length - barStack.index;

      // Calculate the vertical center of the label box.
      const labelY = labelBoxHeight * (labelIndex - 1) - labelBoxHeight / 2 + labelIndex * labelBoxHeight;
      return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, {
        key: `bar-stack-${barStack.index}-${bar.index}`
      }, /*#__PURE__*/_react.default.createElement(_styled2.StyledRect, {
        fill: bar.color,
        height: barHeight,
        rx: rx,
        x: bar.x + chartOffsets.x,
        y: bar.y + chartOffsets.y,
        width: bar.width
      }), /*#__PURE__*/_react.default.createElement(_styled2.StyledCaptionLine, {
        d: `M ${bar.width + lineOffsetX + chartOffsets.x} ${barHeight / 2 + bar.y + chartOffsets.y} l ${lineWidths[0]} 0 L ${bar.width + lineOffsetX + lineWidths[0] + lineWidths[1] + backgroundPadding / 2} ${labelY} l ${lineWidths[2]} 0`
      }));
    }))];
  })), /*#__PURE__*/_react.default.createElement(_styled2.StyledCaptionsContainer, null, children));
}

//
// Sub-components
// -------------------------------------------------------------------------------------------------
SegmentChart.Segment = Segment;
function Segment({
  children,
  testId,
  value
}) {
  return /*#__PURE__*/_react.default.createElement(_styled2.StyledCaption, {
    "data-testid": testId,
    "data-value": value
  }, children);
}

//
// Utility components
// -------------------------------------------------------------------------------------------------

SegmentChart.Loadable = Loadable;
function Loadable({
  children,
  loading,
  placeholderCount,
  ...props
}) {
  var _props$direction;
  const isLoading = (0, _components.useLoadable)(loading);
  if (!isLoading) return /*#__PURE__*/_react.default.createElement(SegmentChart, props, children);

  // Just return a blank box back if no placeholders given
  if (!placeholderCount) return /*#__PURE__*/_react.default.createElement(_base.Box, {
    bg: "$gray300",
    css: {
      animation: `${_animations.pulse} 1.5s linear infinite`
    },
    r: true
  });

  // Let placeholder account be either a string or number
  if (typeof placeholderCount === 'string') placeholderCount = Number.parseFloat(placeholderCount);
  const segments = Array.from({
    length: placeholderCount
  }, (item, index) => {
    // Generate random values and placeholder text to give the idea of varied data within the chart
    const text = Math.random().toString(36).substring(2, Math.floor(Math.random() * 5 + 10));
    const value = Math.floor(Math.random() * 90 + 10);
    return /*#__PURE__*/_react.default.createElement(SegmentChart.Segment, {
      color: "gray300",
      key: index,
      value: value
    }, /*#__PURE__*/_react.default.createElement(_components.TextBlock.Loadable, {
      loading: isLoading
    }, text));
  });
  const HideHorizontalLinesFix = (0, _styled.styled)('div', {
    [`&.horizontal ${_styled2.StyledCaptionLine}`]: {
      display: 'none'
    }
  });
  // Couldn't use any other method because typescript and stitches hate when a prop can be of type undefined when attempting to make a variant, even if unused
  return /*#__PURE__*/_react.default.createElement(HideHorizontalLinesFix, {
    className: (_props$direction = props.direction) === null || _props$direction === void 0 ? void 0 : _props$direction.toString()
  }, /*#__PURE__*/_react.default.createElement(SegmentChart, props, segments));
}

//
// Targeted style components
// -------------------------------------------------------------------------------------------------
SegmentChart.Handle = _styled2.LineHandle;
SegmentChart.Background = _styled2.StyledBackground;